import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { extractDataFromFile, extractTextFromImage } from '../lib/openai';
import ClientService, { DataKey } from '../services/clientService';
import { AuthService } from '../services/authService';
import { DemographicsData, PersonalValue } from '../types';

interface FileUploadProps {
  clientId: string;
  onDataExtracted: (data: any, dataType: string) => void;
  onClose: () => void;
  analysisHint?: string; // optional strong hint like "Site Affinity" | "Category Affinity" | etc.
  audienceId?: string; // optional audience ID for multi-audience support
}

interface UploadedFile {
  id: string;
  file: File;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  result?: any;
  error?: string;
  meta?: { publicUrl: string; fileType: string; text?: string };
}

const PERSONAL_VALUE_LABELS = [
  'Tolerance',
  'Nature',
  'Equality',
  'Dependability',
  'Caring',
  'Humility',
  'Conformity',
  'Duty',
  'Tradition',
  'Security',
  'Safety',
  'Reputation',
  'Influence',
  'Authority',
  'Achievement',
  'Pleasure',
  'Stimulation',
  'Independence',
  'Creativity',
] as const;

export default function FileUpload({ clientId, onDataExtracted, onClose, analysisHint, audienceId }: FileUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'uploading' as const,
      progress: 0
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Process each file
    newFiles.forEach(uploadedFile => {
      processFile(uploadedFile);
    });
  }, [clientId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'],
      'text/csv': ['.csv'],
      'application/json': ['.json']
    },
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  const fileToDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processFile = async (uploadedFile: UploadedFile) => {
    try {
      // Update status to uploading
      updateFileStatus(uploadedFile.id, 'uploading', 25);

      // Upload file to Supabase storage
      const fileExt = uploadedFile.file.name.split('.').pop();
      const fileName = `${clientId}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('client-files')
        .upload(fileName, uploadedFile.file);

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      updateFileStatus(uploadedFile.id, 'uploading', 50);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('client-files')
        .getPublicUrl(fileName);

      updateFileStatus(uploadedFile.id, 'processing', 75);

      // Process file based on type
      let extractedData;
      const fileType = uploadedFile.file.type;

      const hintText = buildAnalysisPrefix(analysisHint);

      if (fileType.startsWith('image/')) {
        const dataUrl = await fileToDataUrl(uploadedFile.file);
        const parts: any[] = [];
        if (hintText) parts.push({ type: 'text', text: hintText });
        parts.push({ type: 'image_url', image_url: { url: dataUrl } });
        extractedData = await extractDataFromFile(parts as any, 'image', clientId);
        if ((extractedData as any)?.error) throw new Error((extractedData as any).error);

        // Personal Values images sometimes return only Top 3 entries.
        // Run a focused OCR pass to recover wheel values when coverage is low.
        if ((analysisHint || '').toLowerCase().includes('personal')) {
          const existingValues = ensurePersonalValuesArray(
            (extractedData as any)?.personal_values ??
            (extractedData as any)?.personalValues ??
            extractPersonalValuesFromSectionsLocal(
              ensureArray(
                (extractedData as any)?.sections ??
                (extractedData as any)?.data?.sections
              )
            )
          );

          if (existingValues.length < 10) {
            try {
              const ocrText = await extractTextFromImage(dataUrl);
              const ocrValues = extractPersonalValueIndexesFromText(ocrText);

              if (ocrValues.length >= 10) {
                const merged = new Map<string, PersonalValue>();
                existingValues.forEach((item) => {
                  const key = normalizeKey(item.name);
                  if (key) merged.set(key, item);
                });
                ocrValues.forEach((item) => {
                  const key = normalizeKey(item.name);
                  if (!key) return;
                  const prior = merged.get(key);
                  merged.set(key, {
                    name: item.name,
                    value: item.value,
                    category: prior?.category ?? item.category ?? normalizeValueCategoryLocal(item.name),
                    longTitle: prior?.longTitle,
                    description: prior?.description,
                    tip: prior?.tip,
                  });
                });
                (extractedData as any).personal_values = Array.from(merged.values());
              }
            } catch (ocrError) {
              console.warn('Personal Values OCR fallback failed:', ocrError);
            }
          }
        }
        uploadedFile.meta = { publicUrl, fileType: 'image' };
      } else if (fileType === 'application/pdf') {
        // For PDFs, we'd need a PDF parser (not implemented in this demo)
        // For now, we'll show an error
        throw new Error('PDF processing not yet implemented. Please convert to image or use CSV/JSON files.');
      } else if (fileType === 'text/csv' || uploadedFile.file.name.toLowerCase().endsWith('.csv')) {
        // Parse Site/Category CSV directly without OpenAI for reliability
        const text = await uploadedFile.file.text();
        const rows = parseCsv(text);
        let dataPayload: any = null;
        if ((analysisHint || '').toLowerCase().includes('site')) {
          // Normalize for Site Affinity renderer
          dataPayload = rows.map(r => ({
            label: String((r['Domain Name'] ?? r['Domain'] ?? '') || ''),
            value: Number(r['UV Index'] ?? r['Index'] ?? 0)
          })).filter(r => r.label);
          extractedData = { sections: [{ title: 'Site Affinity', order: 50, data: dataPayload }], insights: [] };
        } else if ((analysisHint || '').toLowerCase().includes('category')) {
          // Parse into MANY charts: one per distinct "Category Name" with subcategories as items
          const lower = (s: string) => s.toLowerCase();
          const findKey = (obj: Record<string, any>, candidates: string[]) => {
            const keys = Object.keys(obj);
            // exact match first
            for (const want of candidates) {
              const k = keys.find(h => lower(h) === lower(want));
              if (k) return k;
            }
            // loose contains match second
            for (const want of candidates) {
              const k = keys.find(h => lower(h).includes(lower(want)));
              if (k) return k;
            }
            return null;
          };
          const parseNum = (v: any) => {
            if (typeof v === 'number') return v;
            if (typeof v === 'string') {
              const n = Number(v.replace(/[^0-9.\-]/g, ''));
              return Number.isFinite(n) ? n : 0;
            }
            return 0;
          };
          const categoryKey = rows.length ? (findKey(rows[0] as any, ['Category Name', 'Category']) || 'Category') : 'Category';
          const subcatKey = rows.length ? (findKey(rows[0] as any, ['Sub Category Name', 'Subcategory Name']) || null) : null;
          const catIndexKey = rows.length ? findKey(rows[0] as any, ['Category Index', 'Index', 'Affinity', 'Score']) : null;
          const subIndexKey = rows.length ? findKey(rows[0] as any, ['Sub Category Index', 'Index', 'Affinity', 'Score']) : null;

          const byCategory = new Map<string, Array<{ label: string; value: number }>>();
          rows.forEach((r) => {
            const cat = String((r as any)[categoryKey] ?? '').trim();
            if (!cat) return;
            const sub = subcatKey ? String((r as any)[subcatKey] ?? '').trim() : '';
            const valRaw = sub ? ((subIndexKey ? (r as any)[subIndexKey] : undefined)) : ((catIndexKey ? (r as any)[catIndexKey] : undefined));
            const val = parseNum(valRaw);

            const arr = byCategory.get(cat) || [];
            const label = sub || cat; // prefer subcategory; fallback to category row itself
            // aggregate by max within a category
            const existing = arr.find(x => x.label === label);
            if (existing) existing.value = Math.max(existing.value, val);
            else arr.push({ label, value: val });
            byCategory.set(cat, arr);
          });

          const sectionsOut = Array.from(byCategory.entries()).map(([cat, arr], idx) => {
            // filter empties; if all zeros, still show labels with zeros to keep presence
            const items = arr.filter(x => x.label);
            // Create Brand-Affinity-like “Top …” cards for each category
            return { title: `Top ${cat}`, order: 51 + idx, data: items };
          });

          extractedData = { sections: sectionsOut, insights: [] };
        } else if (
          (analysisHint || '').toLowerCase().includes('psychological') ||
          (analysisHint || '').toLowerCase().includes('hobbies') ||
          (analysisHint || '').toLowerCase().includes('habit') ||
          (analysisHint || '').toLowerCase().includes('routine') ||
          (analysisHint || '').toLowerCase().includes('personal')
        ) {
          // Parse Insight lists directly
          const hint = (analysisHint || '').toLowerCase();
          const rows = parseCsv(text);
          const findKey = (obj: Record<string, any>, candidates: string[]) => {
            const keys = Object.keys(obj);
            const lower = (s: string) => s.toLowerCase();
            // Try exact match first
            for (const want of candidates) {
              const k = keys.find((h) => lower(h) === lower(want));
              if (k) return k;
            }
            // Then try contains match
            for (const want of candidates) {
              const k = keys.find((h) => lower(h).includes(lower(want)));
              if (k) return k;
            }
            return null;
          };

          const parseNum = (v: any) => {
            if (typeof v === 'number') return v;
            if (typeof v === 'string') {
              const n = Number(v.replace(/[^0-9.\-]/g, ''));
              return Number.isFinite(n) ? n : 0;
            }
            return 0;
          };

          // Prioritize 'Insight Value' column if present, as it typically contains the cleaner label
          const insightKeyCandidates = ['Insight Value', 'Insight', 'Label', 'Name', 'Activity', 'Driver', 'Habit', 'Routine'];
          const insightKey =
            rows.length > 0
              ? findKey(rows[0] as any, insightKeyCandidates)
              : null;
          const indexKey = rows.length > 0 ? findKey(rows[0] as any, ['Index', 'Score', 'Value']) : null;
          const compKey =
            rows.length > 0
              ? findKey(rows[0] as any, ['Composition', 'Percent', 'Percentage', '%', 'Share'])
              : null;

          const data = rows
            .map((r) => {
              const insight = insightKey ? String((r as any)[insightKey] || '').trim() : String(Object.values(r)[0] || '');
              const index = indexKey ? parseNum((r as any)[indexKey]) : 0;
              const composition = compKey ? parseNum((r as any)[compKey]) : 0;
              return { insight, index, composition };
            })
            .filter((item) => item.insight && item.index > 0);

          if (hint.includes('psychological')) {
            extractedData = { psychologicalDrivers: data };
          } else if (hint.includes('hobbies') || hint.includes('habit')) {
            extractedData = { hobbiesAndHabits: data };
          } else if (hint.includes('routine')) {
            extractedData = { dailyRoutine: data };
          } else if (hint.includes('personal')) {
            // Map generic insight format to Personal Value format
            extractedData = {
              personal_values: data.map((d) => ({
                name: d.insight,
                value: d.index,
              })),
            };
          }
        } else {
          // Fallback: send to OpenAI if no hint
          const content = hintText ? `${hintText}\n\n${text}` : text;
          extractedData = await extractDataFromFile(content, 'csv', clientId);
        }
        uploadedFile.meta = { publicUrl, fileType: 'csv', text };
      } else if (fileType === 'application/json') {
        const text = await uploadedFile.file.text();
        const content = hintText ? `${hintText}\n\n${text}` : text;
        extractedData = await extractDataFromFile(content, 'json', clientId);
        uploadedFile.meta = { publicUrl, fileType: 'json', text };
      } else {
        throw new Error('Unsupported file type');
      }

      const datasets = extractDatasetsFromResult(extractedData, analysisHint);
      const entries = Object.entries(datasets) as Array<[DataKey, any]>;

      for (const [key, value] of entries) {
        try {
          await ClientService.updateClientData(clientId, key, value, audienceId);
          onDataExtracted(value, key);
        } catch (err) {
          console.error(`Failed to persist dataset ${key}`, err);
        }
      }

      updateFileStatus(uploadedFile.id, 'completed', 100, extractedData);

      try {
        const session = AuthService.getSession();
        if (session?.token) {
          const { error: logError } = await supabase.functions.invoke('admin-client-write', {
            headers: { 'x-admin-session': session.token },
            body: {
              operation: 'insert',
              table: 'file_uploads',
              data: {
                client_id: clientId,
                filename: uploadedFile.file.name,
                file_url: publicUrl,
                file_type: fileExt || 'unknown',
                status: 'completed',
                processing_result: extractedData,
              },
            },
          });
          if (logError) {
            console.error('File upload log insert failed:', logError);
          }
        }
      } catch (logErr) {
        console.error('File upload log insert failed:', logErr);
      }
    } catch (error) {
      console.error('File processing error:', error);
      updateFileStatus(uploadedFile.id, 'error', 100, undefined, error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const updateFileStatus = (
    fileId: string, 
    status: UploadedFile['status'], 
    progress: number, 
    result?: any, 
    error?: string
  ) => {
    setUploadedFiles(prev => prev.map(file => 
      file.id === fileId 
        ? { ...file, status, progress, result, error }
        : file
    ));
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const buildAnalysisPrefix = (hint?: string) => {
    if (!hint) return '';
    const h = hint.toLowerCase();
    if (h.includes('site')) return 'Analyze as SITE AFFINITY: treat first string column as site/domain and any numeric columns like "UV Index", "QVI Audience", "Index", or "Affinity" as values. Return a "Site Affinity" section with rows.';
    if (h.includes('category')) return 'Analyze as CATEGORY AFFINITY: treat first string column as category and numeric columns as values. Return a "Category Affinity" section with rows.';
    if (h.includes('aeo') || h.includes('geo') || h.includes('seo')) return 'Analyze as AEO/GEO/SEO: Extract the bubble chart titled like "Share of Voice vs. Sentiment". Return a section titled "AEO/GEO/SEO" with data { "points": [{"name","shareOfVoice","sentiment","size"?}] }. Include all brands/competitors from the legend. If present in the document, also extract any Opportunities text as HTML to data.opportunitiesText.';
    if (h.includes('brand')) return 'Analyze as BRAND AFFINITY: extract brand rows with index and composition.';
    if (h.includes('personal')) return 'Analyze as PERSONAL VALUES.';
    if (h.includes('psychological')) return 'Analyze as PSYCHOLOGICAL DRIVERS: capture every row with insight label, index, and percent composition. Populate both the sections array (title "Psychological Drivers") and the top-level key "psychologicalDrivers" with [{ "insight","index","composition" }].';
    if (h.includes('hobbies') || h.includes('habit')) return 'Analyze as HOBBIES & HABITS: capture each hobby/habit insight with index and percent composition. Populate "Hobbies & Habits" section and the top-level key "hobbiesAndHabits".';
    if (h.includes('routine')) return 'Analyze as DAILY ROUTINE: capture each routine insight with index and percent composition. Populate "Daily Routine" section and the top-level key "dailyRoutine".';
    if (h.includes('demographic')) return 'Analyze as DEMOGRAPHICS.';
    return hint;
  };

  // Helper: fetch a public URL and return a base64 data URL
  const toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Minimal CSV parser that returns array of objects using first row as headers
  const parseCsv = (csvText: string): Array<Record<string, string | number>> => {
    const lines = csvText.trim().split(/\r\n|\n|\r/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    const rows: Array<Record<string, string | number>> = [];
    for (let i = 1; i < lines.length; i++) {
      const raw = lines[i].trim();
      if (!raw) continue;
      const cols = raw.split(',');
      const obj: Record<string, string | number> = {};
      headers.forEach((h, idx) => {
        const v = (cols[idx] ?? '').trim();
        const n = Number(v.replace(/[^0-9.\-]/g, ''));
        obj[h] = Number.isFinite(n) && v !== '' && /^(?:[-+]?\d*\.?\d+)$/.test(v.replace(/[^0-9.\-]/g, '')) ? n : v;
      });
      rows.push(obj);
    }
    return rows;
  };

  const escapeRegex = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const extractPersonalValueIndexesFromText = (text: string): PersonalValue[] => {
    if (!text || typeof text !== 'string') return [];

    const out = new Map<string, PersonalValue>();
    PERSONAL_VALUE_LABELS.forEach((label) => {
      const escaped = escapeRegex(label);
      const direct = new RegExp(`\\b${escaped}\\b\\s*(?:[:\\-–—]\\s*)?(\\d{1,3})\\b`, 'i');
      const reversed = new RegExp(`\\b(\\d{1,3})\\s*(?:[:\\-–—]\\s*)?${escaped}\\b`, 'i');
      const match = text.match(direct) ?? text.match(reversed);
      if (!match) return;
      const numeric = Number(match[1]);
      if (!Number.isFinite(numeric) || numeric <= 0 || numeric > 500) return;
      out.set(
        normalizeKey(label),
        {
          name: label,
          value: numeric,
          category: normalizeValueCategoryLocal(label),
        }
      );
    });

    return Array.from(out.values());
  };

  function extractDatasetsFromResult(raw: any, hint?: string): Partial<Record<DataKey, any>> {
    const datasets: Partial<Record<DataKey, any>> = {};
    const root = parseMaybeJSON(raw);

    const addDataset = (key: DataKey, value: any) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return;
      datasets[key] = value;
    };

    const sections = ensureArray(
      root?.sections ||
        root?.data?.sections ||
        root?.flexibleSections ||
        root?.data?.flexibleSections ||
        (typeof raw === 'string' ? parseMaybeJSON(raw)?.sections : [])
    );
    const directDemographics = normalizeDemographicsDataset(
      parseMaybeJSON(root?.demographics || root?.audience || root?.data?.demographics)
    );
    const sectionDemographics = buildDemographicsFromFlexibleSections(sections);
    let demographics: DemographicsData | null = null;
    if (directDemographics) {
      demographics = mergeDemographicObjects(demographics, directDemographics);
    }
    if (sectionDemographics) {
      demographics = mergeDemographicObjects(demographics, sectionDemographics);
    }
    if (demographics && hasDemographicContent(demographics)) {
      addDataset('demographics', demographics);
    }

    if (sections.length > 0) addDataset('flexible_sections', sections);

    addDataset('brand_affinity', parseMaybeJSON(root?.brandAffinity || root?.data?.brandAffinity));
    addDataset('insights', ensureArray(root?.insights || root?.data?.insights));
    addDataset('custom_charts', ensureArray(root?.customCharts || root?.data?.customCharts));

    let personalValuesCandidate =
      root?.personalValues ||
      root?.personal_values ||
      root?.values ||
      root?.data?.personalValues ||
      root?.data?.personal_values ||
      root?.data?.values;

    if (personalValuesCandidate === undefined && Array.isArray(root)) {
      personalValuesCandidate = root;
    }

    if (personalValuesCandidate === undefined && typeof root === 'string') {
      personalValuesCandidate = root;
    }

    const personalValuesDirect = ensurePersonalValuesArray(personalValuesCandidate);
    if (personalValuesDirect.length > 0) {
      addDataset('personal_values', personalValuesDirect);
    } else if (sections.length > 0) {
      const derived = extractPersonalValuesFromSectionsLocal(sections);
      if (derived.length > 0) addDataset('personal_values', derived);
    }

    const psychDirect = ensureArray(
      root?.psychologicalDrivers ||
        root?.psychological_drivers ||
        root?.data?.psychologicalDrivers ||
        root?.data?.psychological_drivers
    );
    if (psychDirect.length > 0) addDataset('psychological_drivers', psychDirect);

    const hobbiesDirect = ensureArray(
      root?.hobbiesAndHabits ||
        root?.hobbies_habits ||
        root?.data?.hobbiesAndHabits ||
        root?.data?.hobbies_habits
    );
    if (hobbiesDirect.length > 0) addDataset('hobbies_habits', hobbiesDirect);

    const routineDirect = ensureArray(
      root?.dailyRoutine || root?.daily_routine || root?.data?.dailyRoutine || root?.data?.daily_routine
    );
    if (routineDirect.length > 0) addDataset('daily_routine', routineDirect);

    if (!datasets.personal_values && hint && hint.toLowerCase().includes('personal')) {
      const fallback = extractPersonalValuesFromSectionsLocal(sections);
      if (fallback.length > 0) addDataset('personal_values', fallback);
    }

    const rules = getHintRules(hint);
    const allowedKeys = rules?.datasets?.length ? new Set<DataKey>(rules.datasets) : null;
    const allowedSectionTitles = rules?.sectionTitles?.map((title) => normalizeKey(title)) ?? null;

    const filtered: Partial<Record<DataKey, any>> = {};

    (Object.entries(datasets) as Array<[DataKey, any]>).forEach(([key, value]) => {
      if (allowedKeys && !allowedKeys.has(key)) return;
      let nextValue = value;
      if (key === 'flexible_sections' && Array.isArray(value) && allowedSectionTitles && allowedSectionTitles.length > 0) {
        nextValue = value.filter((section) => {
          const normalized = normalizeKey(section?.title || '');
          return allowedSectionTitles.some((title) => normalized.includes(title));
        });
        if (nextValue.length === 0) return;
      }
      filtered[key] = nextValue;
    });

    return filtered;
  }

  function getHintRules(hint?: string) {
    if (!hint) return null;
    const normalized = hint.toLowerCase().trim();

    const rulesMap: Record<string, { datasets: DataKey[]; sectionTitles: string[] }> = {
      'demographics': { datasets: ['demographics', 'flexible_sections'], sectionTitles: ['audience', 'education', 'employment status'] },
      'personal values': { datasets: ['personal_values', 'flexible_sections'], sectionTitles: ['personal values'] },
      'psychological drivers': { datasets: ['psychological_drivers', 'flexible_sections'], sectionTitles: ['psychological drivers'] },
      'hobbies & habits': { datasets: ['hobbies_habits', 'flexible_sections'], sectionTitles: ['hobbies & habits', 'hobbies and habits'] },
      'daily routine': { datasets: ['daily_routine', 'flexible_sections'], sectionTitles: ['daily routine'] },
      'brand affinity': { datasets: ['brand_affinity', 'flexible_sections'], sectionTitles: ['brand affinity'] },
      'site affinity': { datasets: ['flexible_sections'], sectionTitles: ['site affinity'] },
      'aeo/geo/seo': { datasets: ['flexible_sections'], sectionTitles: ['aeo/geo/seo'] },
    };

    return rulesMap[normalized] ?? null;
  }

  function parseMaybeJSON(value: any) {
    if (value === null || value === undefined) return value;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return value;
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }
    return value;
  }

  function ensureArray(value: any): any[] {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const parsed = parseMaybeJSON(value);
      return Array.isArray(parsed) ? parsed : [];
    }
    if (typeof value === 'object') {
      return Array.isArray(value.values) ? value.values : Array.isArray(value.items) ? value.items : [];
    }
    return [];
  }

  function normalizeValueCategoryLocal(name: string): PersonalValue['category'] {
    const mapping: Record<string, PersonalValue['category']> = {
      nature: 'openness',
      creativity: 'openness',
      stimulation: 'openness',
      independence: 'openness',
      pleasure: 'openness',
      influence: 'self-enhancement',
      achievement: 'self-enhancement',
      authority: 'self-enhancement',
      reputation: 'self-enhancement',
      equality: 'self-transcendence',
      tolerance: 'self-transcendence',
      caring: 'self-transcendence',
      dependability: 'conservation',
      conformity: 'conservation',
      safety: 'conservation',
      security: 'conservation',
      duty: 'conservation',
      tradition: 'conservation',
      humility: 'conservation',
    };
    const key = name.toLowerCase().replace(/[^a-z]/g, '');
    return mapping[key] ?? 'openness';
  }

  const firstString = (...candidates: any[]): string | undefined => {
    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) return trimmed;
      }
    }
    return undefined;
  };

  function ensurePersonalValuesArray(value: any): PersonalValue[] {
    const arr = ensureArray(value);
    if (!arr.length) return [];
    const seen = new Map<string, PersonalValue>();
    arr.forEach((item: any) => {
      const name = (item?.name || item?.label || item?.title || '').toString().trim();
      const rawValue =
        item?.value ?? item?.index ?? item?.score ?? item?.amount ?? item?.percentage ?? null;
      const numeric =
        typeof rawValue === 'number'
          ? rawValue
          : typeof rawValue === 'string'
            ? Number(rawValue.replace(/[^0-9.\-]/g, ''))
            : Number.NaN;
      if (!name || Number.isNaN(numeric)) return;
      const key = name.toLowerCase().replace(/\s+/g, ' ');
      const category =
        typeof item?.category === 'string'
          ? item.category
          : normalizeValueCategoryLocal(name);
      const descriptor = firstString(
        item?.descriptor,
        item?.descriptorTitle,
        item?.descriptive_title,
        item?.subtitle,
        item?.subTitle,
        item?.heading,
        item?.longTitle,
        item?.long_title
      );
      const description = firstString(
        item?.description,
        item?.summary,
        item?.narrative,
        item?.detail,
        item?.body,
        item?.text
      );
      const tip = firstString(
        item?.tip,
        item?.recommendation,
        item?.guidance,
        item?.takeaway,
        item?.keyTakeaway,
        item?.key_takeaway,
        item?.insight
      );
      const existing = seen.get(key);
      const base: PersonalValue = existing
        ? { ...existing }
        : { name, value: numeric, category };
      // Latest parsed value should win for this upload; do not preserve stale higher values.
      base.value = numeric;
      if (!base.longTitle && descriptor) base.longTitle = descriptor;
      if (!base.description && description) base.description = description;
      if (!base.tip && tip) base.tip = tip;
      seen.set(key, base);
    });
    return Array.from(seen.values());
  }

  function extractPersonalValuesFromSectionsLocal(sections: any[]): PersonalValue[] {
    if (!sections || sections.length === 0) return [];
    const values = new Map<string, PersonalValue>();

    sections.forEach((section) => {
      if (!section || typeof section !== 'object') return;
      const title = String(section.title || '').toLowerCase();
      if (!title.includes('personal value')) return;

      const visit = (node: any) => {
        if (!node) return;
        if (Array.isArray(node)) {
          node.forEach(visit);
          return;
        }
        if (typeof node !== 'object') return;

        const name = node.name || node.label || node.title;
        const rawValue =
          node.value ?? node.index ?? node.score ?? node.amount ?? node.percentage ?? null;
        const numeric =
          typeof rawValue === 'number'
            ? rawValue
            : typeof rawValue === 'string'
              ? Number(rawValue.replace(/[^0-9.\-]/g, ''))
              : Number.NaN;
        if (name && !Number.isNaN(numeric)) {
          const key = name.toString().trim().toLowerCase().replace(/\s+/g, ' ');
          const category = normalizeValueCategoryLocal(name.toString());
          const descriptor = firstString(
            node?.descriptor,
            node?.descriptorTitle,
            node?.descriptive_title,
            node?.subtitle,
            node?.subTitle,
            node?.heading,
            node?.longTitle,
            node?.long_title
          );
          const description = firstString(
            node?.description,
            node?.summary,
            node?.narrative,
            node?.detail,
            node?.body,
            node?.text
          );
          const tip = firstString(
            node?.tip,
            node?.recommendation,
            node?.guidance,
            node?.takeaway,
            node?.keyTakeaway,
            node?.key_takeaway,
            node?.insight
          );
          const existing = values.get(key);
          const base: PersonalValue = existing
            ? { ...existing }
            : { name: name.toString(), value: numeric, category };
          // Latest parsed value should win for this upload; do not preserve stale higher values.
          base.value = numeric;
          if (!base.longTitle && descriptor) base.longTitle = descriptor;
          if (!base.description && description) base.description = description;
          if (!base.tip && tip) base.tip = tip;
          values.set(key, base);
        }

        Object.values(node).forEach((child) => visit(child));
      };

      visit(section.data ?? section.values ?? section.items ?? section);
    });

    return Array.from(values.values());
  }

  function normalizeDemographicsDataset(raw: any): Partial<DemographicsData> | null {
    if (!isPlainObject(raw)) return null;
    const result: Partial<DemographicsData> = {};
    let touched = false;

    const metrics = isPlainObject(raw.metrics) ? raw.metrics : undefined;
    const targetable = normalizeTargetableIds(raw.targetableIds ?? metrics?.projectedAdultPopulation);
    if (targetable) {
      result.targetableIds = targetable;
      touched = true;
    }

    const genderSource = raw.gender || raw.data?.gender;
    const ageSource = raw.ageGroups || raw.age || raw.data?.age || raw.data?.ageGroups;
    const educationSource = raw.education || raw.data?.education;
    const employmentSource = raw.employment || raw.data?.employment;
  const incomeSource = raw.income || raw.data?.income;
  const maritalSource = raw.maritalStatus || raw.marital || raw.data?.maritalStatus || raw.data?.marital;
  const childrenSource = raw.children || raw.data?.children;

  const gender = normalizeDistribution(genderSource, GENDER_KEY_MAP);
    if (Object.keys(gender).length > 0) {
      result.gender = gender as DemographicsData['gender'];
      touched = true;
    }

    const ages = normalizeAgeGroups(ageSource);
    if (ages) {
      result.ageGroups = ages;
      touched = true;
    }

    const education = normalizeDistribution(educationSource, EDUCATION_KEY_MAP);
    if (Object.keys(education).length > 0) {
      result.education = education as DemographicsData['education'];
      touched = true;
    }

    const employment = normalizeEmployment(employmentSource);
    if (Object.keys(employment).length > 0) {
      result.employment = employment as DemographicsData['employment'];
      touched = true;
    }

    const income = normalizeDistribution(incomeSource, INCOME_KEY_MAP);
    if (Object.keys(income).length > 0) {
      result.income = income as DemographicsData['income'];
      touched = true;
    }

    const marital = normalizeDistribution(maritalSource, MARITAL_KEY_MAP);
    if (Object.keys(marital).length > 0) {
      result.maritalStatus = marital as DemographicsData['maritalStatus'];
      touched = true;
    }

    const children = normalizeDistribution(childrenSource, CHILDREN_KEY_MAP);
    if (Object.keys(children).length > 0) {
      result.children = children as DemographicsData['children'];
      touched = true;
    }

  return touched ? result : null;
}

 function normalizeAgeGroups(source: any): DemographicsData['ageGroups'] | null {
    const canonical = normalizeDistribution(source, AGE_KEY_MAP);
    if (!canonical || Object.keys(canonical).length === 0) return null;

    const order: Array<keyof DemographicsData['ageGroups']> = [
      '18-24',
      '25-34',
      '35-44',
      '45-49',
      '50-54',
      '55-64',
      '65+',
    ];

    const values = order.map((key) => sanitizeNumeric((canonical as any)[key]));
    const nonNullCount = values.filter((value) => value !== null && value !== undefined).length;
    const needsShift =
      nonNullCount >= 6 &&
      (values[0] ?? 0) > 0 &&
      (values[1] ?? 0) >= 20 &&
      (values[2] ?? 0) !== null;

    const result: Record<string, number> = {};

    if (needsShift) {
      result['18-24'] = 0;
      result['25-34'] = values[0] ?? 0;
      result['35-44'] = values[1] ?? 0;
      result['45-49'] = values[2] ?? 0;
      result['50-54'] = values[4] ?? values[3] ?? 0;
      result['55-64'] = values[5] ?? values[4] ?? 0;
      result['65+'] = values[6] ?? values[5] ?? 0;
      return result as DemographicsData['ageGroups'];
    }

    order.forEach((key, index) => {
      result[key] = values[index] ?? 0;
    });

    return result as DemographicsData['ageGroups'];
  }

  function normalizeEmployment(source: any): Record<string, number> {
    if (!source) return {};
    const result: Record<string, number> = {};
    const parsed = parseMaybeJSON(source);
    if (!parsed) return result;

    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const label = item.label ?? item.name ?? item.title;
        if (!label) return;
        const key = normalizeEmploymentKey(label);
        if (!key) return;
        const composition =
          sanitizeNumeric(item.composition ?? item.percent ?? item.percentage ?? item.value ?? item.score) ?? null;
        const indexVal = sanitizeNumeric(item.index);
        if (
          item.composition !== undefined ||
          item.percent !== undefined ||
          item.percentage !== undefined ||
          item.value !== undefined ||
          item.score !== undefined
        ) {
          result[key] = composition ?? 0;
        }
        if (indexVal !== null) {
          if (key === 'fullTime') result.index = indexVal;
          result[`${key}Index`] = indexVal;
        }
      });
      return result;
    }

    if (isPlainObject(parsed)) {
      const composition = sanitizeNumeric(
        parsed.composition ?? parsed.percent ?? parsed.percentage ?? parsed.fullTime ?? parsed['% composition']
      );
      const index = sanitizeNumeric(parsed.index ?? parsed.fullTimeIndex ?? parsed.selfEmployedIndex);
      if (composition !== null) result.fullTime = composition;
      if (index !== null) result.index = index;

      Object.entries(parsed).forEach(([label, value]) => {
        const key = normalizeEmploymentKey(label);
        if (!key) return;
        if (isPlainObject(value)) {
          const compositionValue =
            sanitizeNumeric((value as any).composition) ??
            sanitizeNumeric((value as any).percent) ??
            sanitizeNumeric((value as any).percentage) ??
            sanitizeNumeric((value as any).value) ??
            sanitizeNumeric((value as any).score);
          const indexValue = sanitizeNumeric((value as any).index);
          if (
            (value as any).composition !== undefined ||
            (value as any).percent !== undefined ||
            (value as any).percentage !== undefined ||
            (value as any).value !== undefined ||
            (value as any).score !== undefined
          ) {
            result[key] = compositionValue ?? 0;
          }
          if (indexValue !== null) {
            if (key === 'fullTime') result.index = indexValue;
            else result[`${key}Index`] = indexValue;
          }
        } else {
          if (value !== undefined && value !== null) {
            const numeric = sanitizeNumeric(value);
            if (numeric !== null) {
              if (key === 'index') result.index = numeric;
              else result[key] = numeric;
            } else {
              result[key] = 0;
            }
          }
        }
      });
    }

    if (result.fullTime === undefined) {
      if (result.selfEmployed !== undefined) result.fullTime = result.selfEmployed;
      else if (result.partTime !== undefined) result.fullTime = result.partTime;
    }

    return result;
  }

  function normalizeEmploymentKey(label: any): string | null {
    const key = normalizeKey(label);
    if (!key) return null;
    if (key.includes('index') && !key.includes('self') && !key.includes('full')) return 'index';
    if (key.includes('full') && key.includes('time')) return 'fullTime';
    if (key.includes('self') && key.includes('employ')) return 'selfEmployed';
    if (key.includes('part') && key.includes('time')) return 'partTime';
    if (key.includes('unemploy')) return 'unemployed';
    return key.replace(/\s+/g, '');
  }

  function normalizeDistribution(source: any, labelMap: Record<string, string>): Record<string, number> {
    if (!source) return {};
    const result: Record<string, number> = {};
    const parsed = parseMaybeJSON(source);
    if (!parsed) return result;

    const addValue = (label: any, raw: any) => {
      if (!label) return;
      const key = labelMap[normalizeKey(label)];
      if (!key) return;
      const numeric = sanitizeNumeric(raw);
      if (numeric === null) return;
      result[key] = numeric;
    };

    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const candidate =
          item.value ??
          item.percent ??
          item.percentage ??
          item.composition ??
          item.index ??
          item.score ??
          item.amount;
        addValue(item.label ?? item.name ?? item.title ?? item.category, candidate);
      });
      return result;
    }

    if (isPlainObject(parsed)) {
      Object.entries(parsed).forEach(([label, value]) => {
        if (isPlainObject(value)) {
          const candidate =
            value.value ?? value.percent ?? value.percentage ?? value.composition ?? value.index ?? value.score;
          addValue(label, candidate);
        } else {
          addValue(label, value);
        }
      });
    }

    return result;
  }

  function sanitizeNumeric(value: any): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return Number(value);
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.\-]/g, '');
      if (!cleaned) return null;
      const num = Number(cleaned);
      return Number.isFinite(num) ? num : null;
    }
    return null;
  }

  function normalizeKey(label: any): string {
    return String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9+]+/g, ' ')
      .trim();
  }

  function isPlainObject(value: any): value is Record<string, any> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
  }

  function buildDemographicsFromFlexibleSections(sections: any[]): DemographicsData | null {
    if (!sections || sections.length === 0) return null;
    let merged: DemographicsData | null = null;
    let touched = false;

    sections.forEach((section) => {
      if (!section || typeof section !== 'object') return;
      const data = parseMaybeJSON(section.data);
      if (!data) return;
      const title = String(section.title || '').toLowerCase();

      if (title.includes('audience')) {
        const patch = normalizeDemographicsDataset({ ...data, metrics: section.metrics ?? {} });
        if (patch) {
          merged = mergeDemographicObjects(merged, patch);
          touched = true;
        }
        return;
      }

      const tests: Array<{ match: boolean; payload: any }> = [
        { match: title.includes('education'), payload: { education: data } },
        { match: title.includes('employment'), payload: { employment: data } },
        { match: title.includes('income'), payload: { income: data } },
        { match: title.includes('marital'), payload: { marital: data } },
        { match: title.includes('children'), payload: { children: data } },
        { match: title.includes('gender'), payload: { gender: data } },
        { match: title.includes('age'), payload: { age: data } },
      ];

      tests.forEach(({ match, payload }) => {
        if (!match) return;
        const patch = normalizeDemographicsDataset(payload);
        if (patch) {
          merged = mergeDemographicObjects(merged, patch);
          touched = true;
        }
      });
    });

    return touched ? merged : null;
  }

  function mergeDemographicObjects(
    base: DemographicsData | null,
    patch: Partial<DemographicsData> | null | undefined
  ): DemographicsData {
    const next = base ? cloneDemographics(base) : createEmptyDemographics();
    if (!patch || typeof patch !== 'object') return next;

    if (patch.targetableIds) {
      next.targetableIds = String(patch.targetableIds);
    }

    const writeScalarGroup = <T extends Record<string, number>>(
      target: T,
      source: Record<string, any> | undefined
    ) => {
      if (!source) return;
      Object.entries(source).forEach(([key, value]) => {
        if (!(key in target)) return;
        if (value === undefined) return;
        const numeric = sanitizeNumeric(value);
        (target as any)[key] = numeric ?? 0;
      });
    };

    writeScalarGroup(next.gender, patch.gender as Record<string, any> | undefined);
    writeScalarGroup(next.ageGroups, patch.ageGroups as Record<string, any> | undefined);
    writeScalarGroup(next.income, patch.income as Record<string, any> | undefined);
    writeScalarGroup(next.education, patch.education as Record<string, any> | undefined);
    writeScalarGroup(next.maritalStatus, patch.maritalStatus as Record<string, any> | undefined);
    writeScalarGroup(next.children, patch.children as Record<string, any> | undefined);

    if (patch.employment) {
      const employment = patch.employment as Record<string, any>;
      const fullTimeRaw = employment.fullTime ?? employment.full_time ?? employment['full-time'];
      if (fullTimeRaw !== undefined) {
        next.employment.fullTime = sanitizeNumeric(fullTimeRaw) ?? 0;
      }
      const fullTimeIndexRaw = employment.fullTimeIndex ?? employment['full-time-index'];
      if (fullTimeIndexRaw !== undefined) {
        const value = sanitizeNumeric(fullTimeIndexRaw);
        if (value !== null) next.employment.fullTimeIndex = value;
      }

      const selfEmployedRaw = employment.selfEmployed ?? employment['self-employed'];
      if (selfEmployedRaw !== undefined) {
        next.employment.selfEmployed = sanitizeNumeric(selfEmployedRaw) ?? 0;
      }
      const selfEmployedIndexRaw = employment.selfEmployedIndex ?? employment['self-employed-index'];
      if (selfEmployedIndexRaw !== undefined) {
        const value = sanitizeNumeric(selfEmployedIndexRaw);
        if (value !== null) next.employment.selfEmployedIndex = value;
      }

      const indexRaw = employment.index ?? employment.fullTimeIndex ?? employment['full-time-index'];
      if (indexRaw !== undefined) {
        const value = sanitizeNumeric(indexRaw);
        if (value !== null) next.employment.index = value;
      }

      if (employment.retired !== undefined) {
        next.employment.retired = sanitizeNumeric(employment.retired) ?? 0;
      }

      const bestIndex = Math.max(
        next.employment.fullTimeIndex ?? 0,
        next.employment.selfEmployedIndex ?? 0,
        next.employment.index ?? 0
      );
      if (bestIndex > 0) {
        next.employment.index = bestIndex;
      }
    }

    return next;
  }

  function hasDemographicContent(data: DemographicsData): boolean {
    const groupHasValues = (obj: Record<string, number>, baseline = 0) =>
      Object.values(obj).some((value) => Number.isFinite(value) && value !== baseline);

    return (
      groupHasValues(data.gender) ||
      groupHasValues(data.ageGroups) ||
      groupHasValues(data.income) ||
      groupHasValues(data.education) ||
      groupHasValues(data.maritalStatus) ||
      groupHasValues(data.children) ||
      data.employment.fullTime !== 0 ||
      (data.employment.selfEmployed ?? 0) !== 0 ||
      (data.employment.partTime ?? 0) !== 0 ||
      (data.employment.unemployed ?? 0) !== 0 ||
      data.employment.index !== 100
    );
  }

  function createEmptyDemographics(): DemographicsData {
    return {
      targetableIds: '0',
      gender: { male: 0, female: 0 },
      ageGroups: {
        '18-24': 0,
        '25-34': 0,
        '35-44': 0,
        '45-49': 0,
        '50-54': 0,
        '55-64': 0,
        '65+': 0,
      },
      income: {
        '<$25k': 0,
        '$25-50k': 0,
        '$50-75k': 0,
        '$75-100k': 0,
        '$100-150k': 0,
        '$150-200k': 0,
        '$200-250k': 0,
        '$250k+': 0,
      },
      employment: {
        fullTime: 0,
        fullTimeIndex: 0,
        selfEmployed: 0,
        selfEmployedIndex: 0,
        partTime: 0,
        unemployed: 0,
        retired: 0,
        index: 100,
      },
      education: {
        lessThanHighSchool: 0,
        highSchool: 0,
        someCollege: 0,
        collegeDegree: 0,
        postGrad: 0,
      },
      maritalStatus: { single: 0, married: 0, divorced: 0, widowed: 0 },
      children: { none: 0, one: 0, twoOrThree: 0, fourOrFive: 0, sixPlus: 0 },
    };
  }

  function cloneDemographics(value: DemographicsData): DemographicsData {
    return {
      targetableIds: value.targetableIds,
      gender: { ...value.gender },
      ageGroups: { ...value.ageGroups },
      income: { ...value.income },
      employment: { ...value.employment },
      education: { ...value.education },
      maritalStatus: { ...value.maritalStatus },
      children: { ...value.children },
    };
  }

  function normalizeTargetableIds(value: any): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string' && value.trim()) return value.trim();
    const numeric = sanitizeNumeric(value);
    if (numeric === null) return undefined;
    if (numeric >= 1000000) return `${Math.round((numeric / 1000000) * 10) / 10}M`;
    if (numeric >= 1000) return `${Math.round((numeric / 1000) * 10) / 10}K`;
    return String(Math.round(numeric));
  }

  const GENDER_KEY_MAP: Record<string, string> = {
    male: 'male',
    female: 'female',
  };

  const AGE_KEY_MAP: Record<string, string> = {
    '18 24': '18-24',
    '18 to 24': '18-24',
    '25 34': '25-34',
    '25 to 34': '25-34',
    '35 44': '35-44',
    '35 to 44': '35-44',
    '45 49': '45-49',
    '45 to 49': '45-49',
    '50 54': '50-54',
    '50 to 54': '50-54',
    '55 64': '55-64',
    '55 to 64': '55-64',
    '65': '65+',
    '65+': '65+',
    '65 plus': '65+',
    '65 to 74': '65+',
  };

  const EDUCATION_KEY_MAP: Record<string, string> = {
    'less than high school degree': 'lessThanHighSchool',
    'less than high school': 'lessThanHighSchool',
    'high school degree': 'highSchool',
    'high school diploma': 'highSchool',
    'some college': 'someCollege',
    'college degree': 'collegeDegree',
    'post grad degree': 'postGrad',
    'post graduate degree': 'postGrad',
    'graduate degree': 'postGrad',
    'postgrad degree': 'postGrad',
  };

  const INCOME_KEY_MAP: Record<string, string> = {
    '<$25k': '<$25k',
    '<25k': '<$25k',
    'less than 25k': '<$25k',
    '$25k 50k': '$25k-50k',
    '25k 50k': '$25k-50k',
    '$25 50k': '$25k-50k',
    '$50k 75k': '$50k-75k',
    '50k 75k': '$50k-75k',
    '$75k 100k': '$75k-100k',
    '75k 100k': '$75k-100k',
    '$100k 150k': '$100k-150k',
    '100k 150k': '$100k-150k',
    '$150k 200k': '$150k-200k',
    '150k 200k': '$150k-200k',
    '$200k 250k': '$200k-250k',
    '200k 250k': '$200k-250k',
    '$250k+': '$250k+',
    '250k+': '$250k+',
    '$250k plus': '$250k+',
    '250k plus': '$250k+',
  };

  const MARITAL_KEY_MAP: Record<string, string> = {
    single: 'single',
    married: 'married',
    divorced: 'divorced',
    widow: 'widowed',
    widower: 'widowed',
    'widow widower': 'widowed',
    widowed: 'widowed',
  };

  const CHILDREN_KEY_MAP: Record<string, string> = {
    'no children': 'none',
    none: 'none',
    '1 child': 'one',
    '1': 'one',
    one: 'one',
    '2 or 3 children': 'twoOrThree',
    '2 or 3': 'twoOrThree',
    'two or three': 'twoOrThree',
    '4 or 5 children': 'fourOrFive',
    '4 or 5': 'fourOrFive',
    'four or five': 'fourOrFive',
    '6+ children': 'sixPlus',
    '6 plus': 'sixPlus',
    '6 children': 'sixPlus',
  };

  // Removed "Analyze All Together" flow per request; one-file-at-a-time via "Use This File Only" only.

  const useExtractedData = (file: UploadedFile) => {
    if (file.result) {
      // Handle new flexible sections structure
      if (file.result.sections && file.result.sections.length > 0) {
        onDataExtracted(file.result.sections, 'flexible_sections');

        try {
          file.result.sections.forEach((section: any) => {
            const title = String(section?.title || '').toLowerCase();
            const data = section?.data ?? section;
            if (!data) return;
            if (title.includes('demographic')) {
              onDataExtracted(data, 'demographics');
            }
            if (title.includes('personal value')) {
              onDataExtracted(data, 'personal_values');
            }
            if (title.includes('psychological')) {
              onDataExtracted(data, 'psychological_drivers');
            }
            if (title.includes('hobbies') || title.includes('habit')) {
              onDataExtracted(data, 'hobbies_habits');
            }
            if (title.includes('daily routine') || title.includes('routine')) {
              onDataExtracted(data, 'daily_routine');
            }
            if (title.includes('brand affinity') || title.includes('retail') || title.includes('restaurant') || title.includes('hotel') || title.includes('airline')) {
              onDataExtracted(data, 'brand_affinity');
            }
          });
        } catch (err) {
          console.warn('Failed to map sections directly:', err);
        }
      }
      
      // Handle insights
      if (file.result.insights && file.result.insights.length > 0) {
        onDataExtracted(file.result.insights, 'insights');
      }
      
      // Legacy support for old data structure
      const demographicsPayload =
        file.result.demographics ??
        file.result.data?.demographics ??
        file.result.audience ??
        file.result.data?.audience;
      if (demographicsPayload) {
        onDataExtracted(demographicsPayload, 'demographics');
      }
      const personalValuesPayload =
        file.result.personalValues ??
        file.result.personal_values ??
        file.result.values ??
        file.result.data?.personalValues ??
        file.result.data?.personal_values ??
        file.result.data?.values ??
        (typeof file.result === 'string' ? file.result : null);
      if (personalValuesPayload) {
        onDataExtracted(personalValuesPayload, 'personal_values');
      }
      if (file.result.psychologicalDrivers) {
        onDataExtracted(file.result.psychologicalDrivers, 'psychological_drivers');
      }
      if (file.result.hobbiesAndHabits) {
        onDataExtracted(file.result.hobbiesAndHabits, 'hobbies_habits');
      }
      if (file.result.dailyRoutine) {
        onDataExtracted(file.result.dailyRoutine, 'daily_routine');
      }
      if (file.result.brandAffinity) {
        onDataExtracted(file.result.brandAffinity, 'brand_affinity');
      }
      if (file.result.customCharts && file.result.customCharts.length > 0) {
        onDataExtracted(file.result.customCharts, 'custom_charts');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Upload Data Files{analysisHint ? ` — ${analysisHint}` : ''}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Upload Area */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-teal-400 bg-teal-50' 
                : 'border-gray-300 hover:border-teal-400'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            {isDragActive ? (
              <p className="text-teal-600">Drop the files here...</p>
            ) : (
              <div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drag & drop files here, or click to select
                </p>
                <p className="text-sm text-gray-500">
                  Supports PDF, images, CSV, and JSON files (max 10MB)
                </p>
              </div>
            )}
          </div>

          {/* File List */}
          {uploadedFiles.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Processing Files</h3>
                <span className="text-xs text-gray-500">
                  ({uploadedFiles.filter(f => f.status === 'completed').length} completed)
                </span>
              </div>
              <div className="space-y-3">
                {uploadedFiles.map(file => (
                  <div key={file.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <File className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {file.file.name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {file.status === 'uploading' || file.status === 'processing' ? (
                          <Loader className="h-4 w-4 text-teal-600 animate-spin" />
                        ) : file.status === 'completed' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                        <button
                          onClick={() => removeFile(file.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          file.status === 'error' 
                            ? 'bg-red-500' 
                            : file.status === 'completed'
                            ? 'bg-green-500'
                            : 'bg-teal-500'
                        }`}
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {file.status === 'uploading' && 'Uploading...'}
                        {file.status === 'processing' && 'Processing with AI...'}
                        {file.status === 'completed' && 'Processing complete'}
                        {file.status === 'error' && `Error: ${file.error}`}
                      </span>

                      {file.status === 'completed' && file.result && (
                        <button
                          onClick={() => useExtractedData(file)}
                          className="text-xs bg-teal-600 text-white px-2 py-1 rounded hover:bg-teal-700 transition-colors"
                        >
                          Submit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Combined analysis removed */}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
